import { useCallback, useState, useEffect } from 'react';
import { GridApi, GridReadyEvent, ICellRendererParams, RowClassParams } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import CommonButton from '../../../components/common-components/button/CommonButton';
import styles from './Appointments.module.css';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import {
  Plus,
  FileSpreadsheet,
  RotateCw,
  Pencil,
  Trash2,
  Calendar as CalendarIcon,
  List,
  Navigation2,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../hooks/useStore';
import NewAppointmentPopup from './parts/NewAppointmentPopup';
import AppointmentForHospitalTagPopup from './parts/AppointmentForHospitalTagPopup';
import EditAppointmentPopup from './parts/EditAppointmentPopup';
import AppointmentDetailPopup from './parts/AppointmentDetailPopup';
import TodayRoutePopup from './parts/TodayRoutePopup';
import DeleteConfirmationDialog from '../hospital-files/parts/delete/DeleteConfirmationDialog';
import AppointmentCalendar from './parts/AppointmentCalendar';
import { toast } from 'react-toastify';
import { IAppointment } from '../../../models/appointment/AppointmentModel';
import BaseAgGrid, {
  type GridFetchParams,
} from '../../../components/common-components/grid/BaseAgGrid';
import { useAppointmentsColumnDefs, useAppointmentsDefaultColDef } from './Appointments.grid';

const ActionCellRenderer = (props: ICellRendererParams<IAppointment>) => {
  const { popupStore, appointmentStore, authStore } = useStore();
  const isAdmin = authStore.isAdmin;

  const handleEdit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data?.id) {
      // Backend'den tam randevu verisini çek (PersonId, UserId, TagPrefix vb. setlenmesi için)
      const fullAppointment = await appointmentStore.fetchAppointmentById(props.data.id);
      if (fullAppointment) {
        appointmentStore.setSelectedAppointmentToEdit(fullAppointment);
        popupStore.setIsEditAppointmentOpen(true);
      } else {
        // Fallback: grid'deki veriyi kullan
        appointmentStore.setSelectedAppointmentToEdit(props.data);
        popupStore.setIsEditAppointmentOpen(true);
      }
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin) {
      toast.error('Bu işlem için admin yetkisi gereklidir.');
      return;
    }
    if (props.data?.id) {
      appointmentStore.setSelectedAppointmentToDelete(props.data.id);
      popupStore.setIsDeleteAppointmentConfirmationOpen(true);
    }
  };

  const handleOpenGoogleMaps = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data?.hospitalLocation) {
      const encodedLocation = encodeURIComponent(props.data.hospitalLocation);
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedLocation}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  const hasLocation = props.data?.hospitalLocation && props.data.hospitalLocation.trim() !== '';

  return (
    <div className={styles.actionCell}>
      {hasLocation && (
        <button onClick={handleOpenGoogleMaps} className={styles.mapButton} title="Konuma Git">
          <Navigation2 size={20} />
        </button>
      )}
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button
        onClick={handleDelete}
        className={`${styles.deleteButton} ${!isAdmin ? styles.disabledButton : ''}`}
        title={isAdmin ? 'Sil' : 'Bu işlem için admin yetkisi gereklidir'}
        disabled={!isAdmin}
      >
        <Trash2 size={20} />
      </button>
    </div>
  );
};

const Appointments = () => {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [calendarKey, setCalendarKey] = useState(0);
  const { popupStore, appointmentStore } = useStore();

  const fetchRows = useCallback(
    async (params: GridFetchParams) => {
      const result = await appointmentStore.listAppointments(params);
      return { rows: result.rows, lastRow: result.lastRow };
    },
    [appointmentStore]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent<IAppointment>) => {
      appointmentStore.setGridApi(params.api);
    },
    [appointmentStore]
  );

  const getRowStyle = useCallback((params: RowClassParams<IAppointment>) => {
    if (!params.data?.appointmentDate) return undefined;

    const appointmentDate = new Date(params.data.appointmentDate);
    const today = new Date();
    // Sadece tarih karşılaştırması için saat bilgisini sıfırla
    today.setHours(0, 0, 0, 0);
    appointmentDate.setHours(0, 0, 0, 0);

    if (appointmentDate < today) {
      return { backgroundColor: '#f8d7da' };
    }
    return undefined;
  }, []);

  const columnDefs = useAppointmentsColumnDefs(ActionCellRenderer);
  const defaultColDef = useAppointmentsDefaultColDef();

  const handleNewAppointment = () => {
    popupStore.setIsNewAppointmentOpen(true);
  };

  const handleAppointmentForHospitalTag = () => {
    popupStore.setIsAppointmentForHospitalTagOpen(true);
  };

  const handleTodayRoute = () => {
    popupStore.setIsTodayRoutePopupOpen(true);
  };

  const exportToExcel = useCallback(
    async (api: GridApi<IAppointment> | null) => {
      if (!api) return;

      api.showLoadingOverlay();

      try {
        const result = await appointmentStore.listAppointments({
          startRow: 0,
          endRow: 1000000,
          filterModel: api.getFilterModel(),
          sortModel: null,
        });

        if (!result?.rows.length) {
          api.hideOverlay();
          toast.warning("Excel'e aktarılacak veri bulunamadı");
          return;
        }

        const exportData = result.rows.map(item => ({
          ID: item.id || '',
          'Kişi Adı': item.personName || '',
          'Etiket Numarası': item.requestNumber || '',
          Hastane: item.hospitalName || '',
          Kullanıcı: item.userName || item.userId || '',
          'Randevu Tarihi': item.appointmentDate
            ? new Date(item.appointmentDate).toLocaleDateString('tr-TR')
            : '',
          'Başlangıç Saati': item.appointmentStartTime || '',
          'Bitiş Saati': item.appointmentEndTime || '',
          'Cihaz Listesi': item.deviceList || '',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Randevular');
        XLSX.writeFile(wb, `Randevular_${new Date().toISOString().split('T')[0]}.xlsx`);

        toast.success(`${exportData.length} randevu Excel dosyasına aktarıldı`);
      } catch (error) {
        console.error('Excel export error:', error);
        toast.error("Excel'e aktarılırken bir hata oluştu");
      } finally {
        api.hideOverlay();
      }
    },
    [appointmentStore]
  );

  const refreshCalendar = useCallback(() => {
    setCalendarKey(prev => prev + 1);
  }, []);

  const toggleViewMode = () => {
    setViewMode(prev => {
      const next = prev === 'list' ? 'calendar' : 'list';
      if (next === 'calendar') refreshCalendar();
      return next;
    });
  };

  // Popup'lar kapandığında takvimi yenile
  useEffect(() => {
    if (
      viewMode === 'calendar' &&
      !popupStore.isNewAppointmentOpen &&
      !popupStore.isAppointmentForHospitalTagOpen &&
      !popupStore.isEditAppointmentOpen &&
      !popupStore.isTodayRoutePopupOpen
    ) {
      setCalendarKey(prev => prev + 1);
    }
  }, [
    popupStore.isNewAppointmentOpen,
    popupStore.isAppointmentForHospitalTagOpen,
    popupStore.isEditAppointmentOpen,
    popupStore.isTodayRoutePopupOpen,
    viewMode,
  ]);

  return (
    <div className={styles.container}>
      {viewMode === 'list' ? (
        <div className={styles.gridContainer}>
          <BaseAgGrid<IAppointment>
            fetchRows={fetchRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            title="Randevular"
            toolbarLeft={({ api, refresh }) => (
              <>
                <CommonButton
                  type="primary"
                  onClick={handleNewAppointment}
                  className={styles.toolbarBtnCreate}
                >
                  <Plus size={20} />
                  Yeni Randevu
                </CommonButton>
                <CommonButton
                  type="primary"
                  onClick={handleAppointmentForHospitalTag}
                  className={styles.toolbarBtnCreate}
                >
                  <Plus size={20} />
                  Hastane/Etiket'e Randevu
                </CommonButton>
                <CommonButton
                  type="outline"
                  onClick={handleTodayRoute}
                  className={styles.toolbarBtnCreate}
                  leftIcon={<Navigation2 size={18} />}
                >
                  Bugünkü Rota
                </CommonButton>
                <CommonButton
                  type="outline"
                  onClick={toggleViewMode}
                  leftIcon={<CalendarIcon size={18} />}
                  className={styles.toolbarBtnCalendar}
                >
                  Takvim Görünümü
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={() => exportToExcel(api)}
                  leftIcon={<FileSpreadsheet size={18} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={refresh}
                  leftIcon={<RotateCw size={18} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
            getRowStyle={getRowStyle}
            paginationPageSize={20}
          />
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <h2 className={styles.headerTitle}>Randevular</h2>
            <div className={styles.buttonContainer}>
              <CommonButton
                type="primary"
                onClick={handleNewAppointment}
                className={styles.toolbarBtnCreate}
              >
                <Plus size={20} />
                Yeni Randevu
              </CommonButton>
              <CommonButton
                type="primary"
                onClick={handleAppointmentForHospitalTag}
                className={styles.toolbarBtnCreate}
              >
                <Plus size={20} />
                Hastane/Etiket'e Randevu
              </CommonButton>
              <CommonButton
                type="outline"
                onClick={handleTodayRoute}
                className={styles.toolbarBtnCreate}
                leftIcon={<Navigation2 size={18} />}
              >
                Bugünkü Rota
              </CommonButton>
              <CommonButton
                type="outline"
                onClick={toggleViewMode}
                leftIcon={<List size={18} />}
                className={styles.toolbarBtnList}
              >
                Liste Görünümü
              </CommonButton>
              <CommonButton
                type="refresh"
                onClick={refreshCalendar}
                leftIcon={<RotateCw size={18} />}
                className={toolbarStyles.toolbarBtnRefresh}
              >
                Yenile
              </CommonButton>
            </div>
          </div>
          <AppointmentCalendar key={calendarKey} />
        </>
      )}
      {popupStore.isNewAppointmentOpen && <NewAppointmentPopup />}
      {popupStore.isAppointmentForHospitalTagOpen && <AppointmentForHospitalTagPopup />}
      {popupStore.isEditAppointmentOpen && <EditAppointmentPopup />}
      {popupStore.isAppointmentDetailOpen && <AppointmentDetailPopup />}
      {popupStore.isTodayRoutePopupOpen && <TodayRoutePopup />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeleteAppointmentConfirmationOpen}
        onClose={() => {
          popupStore.setIsDeleteAppointmentConfirmationOpen(false);
          appointmentStore.setSelectedAppointmentToDelete(null);
        }}
        onConfirm={async () => {
          if (appointmentStore.selectedAppointmentToDelete) {
            await appointmentStore.deleteAppointment(appointmentStore.selectedAppointmentToDelete);
            popupStore.setIsDeleteAppointmentConfirmationOpen(false);
            appointmentStore.setSelectedAppointmentToDelete(null);
            // Takvim görünümündeyse yenile
            if (viewMode === 'calendar') {
              setCalendarKey(prev => prev + 1);
            }
          }
        }}
        title="Randevu Silme Onayı"
      />
    </div>
  );
};

export default observer(Appointments);
